import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";

import DenseForest from "../../../img/dense-forest.jpg";

import { OPERACOES_PAGE } from "../../operacoes/routes";

import searchIcon from "../../../img/search.svg";
import replaceIcon from "../../../img/replace.svg";
import confirmIcon from "../../../img/confirm.svg";
import statusIcon from "../../../img/status.svg";

import PickStoreModal from "../../../components/modal/pick-store/PickStoreModal";
import RegistrationStatusModal from "../../../components/modal/registration-status/RegistrationStatusModal";
import { getFreelancersControlApi, FreelancerControlRow } from "../freelancerControlApi";
import { StorePublic } from "../../store/storeApi";
import {
  SlotRegistrationStatus,
  listStoreRegistrationsApi,
  type StoreRegistrationItem,
  type ListStoreRegistrationsQuery,
  type UpdateRegistrationStatusDto,
  updateRegistrationStatusApi,
} from "../../../views/scheduling/schedulingApi";

import SubstituteFreelancerModal from "../../../components/modal/substitute-freelancer/SubstituteFreelancerModal";
import ViewFreelancerSubmittedEvaluationsModal from "../../../components/modal/select-freelancers-for-evaluation/ViewFreelancerSubmittedEvaluationsModal";
import { listSubmittedEvaluationsByRegistrationApi } from "../../../views/supervisor/supervisorApi";

import { SLOTS_MANAGER_PAGE } from "../../scheduling/routes";

import "./FreelancerControl.css";

const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));

function formatBrDate(value: string | null | undefined) {
  if (!value) return "-";

  const iso = value.slice(0, 10);
  const [y, m, d] = iso.split("-");

  if (!y || !m || !d) return "-";

  return `${d}/${m}/${y}`;
}

const formatCPF = (cpf: string) => {
  const d = (cpf ?? "").replace(/\D/g, "").padStart(11, "0").slice(-11);
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
};

const Stars: React.FC<{
  value: number | null | undefined;
  onClick?: () => void;
  disabled?: boolean;
}> = ({ value, onClick, disabled }) => {
  const v = typeof value === "number" ? clamp(value, 0, 5) : null;
  const pct = v == null ? 0 : (v / 5) * 100;

  const clickable = typeof onClick === "function";

  return clickable ? (
    <button
      type="button"
      className="Stars"
      title={v == null ? "-" : `${v.toFixed(2)} / 5`}
      onClick={onClick}
      disabled={disabled}
      style={{ background: "transparent", border: 0, padding: 0, cursor: disabled ? "not-allowed" : "pointer" }}
    >
      <div className="StarsBar">
        <div className="StarsBack">★★★★★</div>
        <div className="StarsFront" style={{ width: `${pct}%` }}>
          ★★★★★
        </div>
      </div>
    </button>
  ) : (
    <div className="Stars" title={v == null ? "-" : `${v.toFixed(2)} / 5`}>
      <div className="StarsBar">
        <div className="StarsBack">★★★★★</div>
        <div className="StarsFront" style={{ width: `${pct}%` }}>
          ★★★★★
        </div>
      </div>
    </div>
  );
};

const formatBRL = (v: string | null | undefined) => {
  if (!v) return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);
};

const formatTimeFromIso = (iso: string | null | undefined) => {
  if (!iso) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "America/Sao_Paulo",
  }).format(new Date(iso));
};

const formatSlotRange = (reg: StoreRegistrationItem) => {
  const t1 = formatTimeFromIso(reg.slot?.startAt ?? null);
  const t2 = formatTimeFromIso(reg.slot?.endAt ?? null);
  if (t1 === "-" || t2 === "-") return "-";
  return `${t1}-${t2}`;
};

const formatRegistrationDay = (reg: StoreRegistrationItem | null | undefined) => {
  const raw = (reg as any)?.day ?? ((reg?.slot as any)?.day ?? reg?.slot?.startAt ?? null);
  return formatBrDate(raw);
};

const todayYmd = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

function safeStatusLabel(s: string) {
  return (s ?? "").toUpperCase();
}

function normalizeStatus(s: any): SlotRegistrationStatus {
  const up = safeStatusLabel(String(s ?? ""));
  if (up === "APROVADO") return "APROVADO";
  if (up === "AUSENTE") return "AUSENTE";
  if (up === "CANCELADO") return "CANCELADO";
  return "PENDENTE";
}

function getStatusBadgeColor(status: any): "VERDE" | "LARANJA" | "VERMELHO" | "CINZA" | "AZUL" {
  if (!status) return "CINZA";

  const normalized = normalizeStatus(status);

  if (normalized === "APROVADO") return "VERDE";
  if (normalized === "PENDENTE") return "LARANJA";
  if (normalized === "CANCELADO") return "VERMELHO";
  if (normalized === "AUSENTE") return "AZUL";

  return "CINZA";
}

function isRegistrationSlotCanceled(reg: StoreRegistrationItem, row?: FreelancerControlRow | null) {
  const anyReg = reg as any;
  const slotStatus = safeStatusLabel(String(anyReg?.slot?.status ?? ""));

  if (slotStatus === "CANCELADO") return true;
  if (typeof anyReg?.slotIsCanceled === "boolean") return anyReg.slotIsCanceled;
  if (typeof anyReg?.slot?.isCanceled === "boolean") return anyReg.slot.isCanceled;
  if (anyReg?.slot?.canceledAt || anyReg?.slot?.cancelledAt) return true;

  return !!row?.slotIsCanceled;
}

function getRegistrationStoreName(reg: StoreRegistrationItem, row: FreelancerControlRow) {
  const anyReg = reg as any;

  return (
    anyReg?.slot?.store?.nomeFantasia ??
    anyReg?.slot?.storeName ??
    anyReg?.store?.nomeFantasia ??
    anyReg?.storeName ??
    anyReg?.storeToWorkName ??
    row.storeToWorkName ??
    "-"
  );
}

function getRegistrationFinalValue(
  reg: StoreRegistrationItem,
  row: FreelancerControlRow,
) {
  const status = normalizeStatus((reg as any)?.status);

  if (status !== "APROVADO") return "Sem Valor Definido";

  const anyReg = reg as any;
  const raw =
    anyReg?.finalValue ??
    anyReg?.valorFinal ??
    anyReg?.payment?.totalValue ??
    anyReg?.payment?.finalValue ??
    anyReg?.paymentFinalValue ??
    row.finalValue;

  const formatted = formatBRL(raw == null ? null : String(raw));
  return formatted || "-";
}

const StatusBadge: React.FC<{
  value: "VERDE" | "LARANJA" | "VERMELHO" | "CINZA" | "AZUL";
  title?: string;
  children?: React.ReactNode;
  className?: string;
}> = ({ value, title, children, className }) => {
  const style: React.CSSProperties =
    value === "VERDE"
      ? { background: "#2ecc71" }
      : value === "LARANJA"
      ? { background: "#f39c12" }
      : value === "VERMELHO"
      ? { background: "#e74c3c" }
      : value === "AZUL"
      ? { background: "#3498db" }
      : { background: "#95a5a6" };

  return (
    <span
      className={className}
      title={title}
      style={{
        ...style,
        color: "#fff",
        padding: "6px 10px",
        borderRadius: 999,
        fontWeight: 700,
        fontSize: 12,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        whiteSpace: "nowrap",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      {children}
    </span>
  );
};

const FreelancerControlPage: React.FC = () => {
  const navigate = useNavigate();

  const token = useSelector((s: RootState) => s.auth.accessToken);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<FreelancerControlRow[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalItens, setTotalItens] = useState(0);
  const [regByFreelancerId, setRegByFreelancerId] = useState<Record<string, StoreRegistrationItem>>({});
  const [regListByFreelancerId, setRegListByFreelancerId] = useState<Record<string, StoreRegistrationItem[]>>({});
  const [expandedFreelancerId, setExpandedFreelancerId] = useState<string | null>(null);
  const [editedStatusByRegId, setEditedStatusByRegId] = useState<Record<string, SlotRegistrationStatus>>({});
  const [savingByRegId, setSavingByRegId] = useState<Record<string, boolean>>({});

  const [startDay, setStartDay] = useState<string>(todayYmd());
  const [endDay, setEndDay] = useState<string>(todayYmd());

  const [qInput, setQInput] = useState("");
  const [qApplied, setQApplied] = useState("");

  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [priorityStore, setPriorityStore] = useState<StorePublic | null>(null);

  const [isEvalOpen, setIsEvalOpen] = useState(false);
  const [evalTarget, setEvalTarget] = useState<{
    storeId: string;
    freelancerId: string;
    storeName: string | null;
    freelancerName: string | null;
    freelancerCpf: string | null;
  } | null>(null);

  const [isSubOpen, setIsSubOpen] = useState(false);
  const [selectedSubReg, setSelectedSubReg] = useState<StoreRegistrationItem | null>(null);
  const [subStoreId, setSubStoreId] = useState<string>("");

  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState<{
    row: FreelancerControlRow;
    reg: StoreRegistrationItem;
  } | null>(null);

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItens / limit)),
    [totalItens, limit],
  );

  const load = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const data = await getFreelancersControlApi(token, {
        page,
        limit,
        startDay,
        endDay,
        nome: qApplied,
        priorityStoreId: priorityStore?.id ?? null,
      });

      const items = data.items ?? [];
      const uniqueItems = Array.from(
        new Map(items.map((item) => [item.freelancerId, item])).values(),
      );
      setRows(uniqueItems);
      setTotalItens(uniqueItems.length);

      const storeIds = Array.from(
        new Set(items.filter((i) => i.storeToWorkId).map((i) => i.storeToWorkId as string)),
      );

      const regMap: Record<string, StoreRegistrationItem> = {};
      const regListMap: Record<string, StoreRegistrationItem[]> = {};

      await Promise.all(
        storeIds.map(async (storeId) => {
          const query: ListStoreRegistrationsQuery = { startDay, endDay };
          const regs = await listStoreRegistrationsApi(storeId, token, query);

          (regs ?? []).forEach((reg: any) => {
            const fid = (reg.freelancer as any)?.id ?? reg.freelancerId;
            if (fid) {
              if (!regListMap[fid]) regListMap[fid] = [];
              regListMap[fid].push(reg as StoreRegistrationItem);
            }
          });
        }),
      );

      Object.keys(regListMap).forEach((fid) => {
        regListMap[fid].sort((a, b) => {
          const av = String((a as any)?.day ?? a.slot?.startAt ?? "");
          const bv = String((b as any)?.day ?? b.slot?.startAt ?? "");
          return av.localeCompare(bv);
        });

        regMap[fid] = regListMap[fid][regListMap[fid].length - 1];
      });

      setRegByFreelancerId(regMap);
      setRegListByFreelancerId(regListMap);
      setExpandedFreelancerId(null);
      setEditedStatusByRegId({});
      setSavingByRegId({});
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao carregar controle de freelancers.");
    } finally {
      setLoading(false);
    }
  }, [token, page, limit, startDay, endDay, qApplied, priorityStore?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const applySearch = useCallback(() => {
    const next = qInput.trim();
    setQApplied(next);
    setPage(1);
  }, [qInput]);

  const openEval = useCallback((row: FreelancerControlRow) => {
    if (!row.storeToWorkId) {
      toast.info("Não foi possível identificar a loja desse freelancer.");
      return;
    }

    setEvalTarget({
      storeId: row.storeToWorkId,
      freelancerId: row.freelancerId,
      storeName: row.storeToWorkName ?? null,
      freelancerName: row.fullName ?? null,
      freelancerCpf: row.cpf ?? null,
    });
    setIsEvalOpen(true);
  }, []);

  const closeEval = () => {
    setIsEvalOpen(false);
    setEvalTarget(null);
  };

  const openSub = useCallback((row: FreelancerControlRow, reg: StoreRegistrationItem) => {
    if (!token) return;

    const regId = String(reg?.id ?? "");
    const storeId = reg.slot?.storeId ?? row.storeToWorkId ?? "";

    if (!regId) {
      toast.info("Este freelancer não possui inscrição no período selecionado.");
      return;
    }

    if (!storeId) {
      toast.info("Não foi possível identificar a loja desse agendamento.");
      return;
    }

    if (isRegistrationSlotCanceled(reg, row)) {
      toast.info("Slot cancelado.");
      return;
    }

    setSubStoreId(storeId);
    setSelectedSubReg(reg);
    setIsSubOpen(true);
  }, [token]);

  const closeSub = () => {
    setIsSubOpen(false);
    setSelectedSubReg(null);
    setSubStoreId("");
  };

  const openStatus = useCallback((row: FreelancerControlRow, reg: StoreRegistrationItem) => {
    const regId = String(reg?.id ?? "");

    if (!regId) {
      toast.info("Este freelancer não possui inscrição no período selecionado.");
      return;
    }

    if (isRegistrationSlotCanceled(reg, row)) {
      toast.info("Slot cancelado.");
      return;
    }

    setStatusTarget({ row, reg });
    setIsStatusOpen(true);
  }, []);

  const closeStatus = () => {
    setIsStatusOpen(false);
    setStatusTarget(null);
  };

  const handleChangePresence = (regId: string, next: SlotRegistrationStatus) => {
    setEditedStatusByRegId((prev) => ({ ...prev, [regId]: next }));
  };

  const handleConfirmRegistration = async (
    row: FreelancerControlRow,
    reg: StoreRegistrationItem,
    nextStatus?: SlotRegistrationStatus,
  ) => {
    if (!token) return;

    const regId = String(reg?.id ?? "");

    if (!regId) {
      toast.info("Este freelancer não possui inscrição no período selecionado.");
      return;
    }

    const current = normalizeStatus((reg as any)?.status);
    const target = nextStatus ?? editedStatusByRegId[regId] ?? current;

    if (safeStatusLabel(current) === safeStatusLabel(target)) {
      toast.info("Nenhuma alteração para salvar nesta inscrição.");
      setEditedStatusByRegId((prev) => {
        const cp = { ...prev };
        delete cp[regId];
        return cp;
      });
      return;
    }

    try {
      setSavingByRegId((p) => ({ ...p, [regId]: true }));

      const dto: UpdateRegistrationStatusDto = { status: target };
      await updateRegistrationStatusApi(regId, token, dto);

      toast.success("Status atualizado com sucesso.");

      setRegListByFreelancerId((prev) => {
        const cp = { ...prev };
        const arr = [...(cp[row.freelancerId] ?? [])];
        const idx = arr.findIndex((x) => String(x?.id ?? "") === regId);

        if (idx >= 0) arr[idx] = { ...arr[idx], status: target } as any;
        cp[row.freelancerId] = arr;

        return cp;
      });

      setRegByFreelancerId((prev) => {
        const primary = prev[row.freelancerId];

        if (String(primary?.id ?? "") !== regId) return prev;

        return {
          ...prev,
          [row.freelancerId]: { ...primary, status: target } as any,
        };
      });

      setRows((prev) =>
        prev.map((x) =>
          x.freelancerId === row.freelancerId && String(regByFreelancerId[row.freelancerId]?.id ?? "") === regId
            ? ({ ...x, registrationStatus: target } as any)
            : x,
        ),
      );

      setEditedStatusByRegId((prev) => {
        const cp = { ...prev };
        delete cp[regId];
        return cp;
      });
    } catch {
      toast.error("Erro ao salvar alterações. Verifique permissões e dados.");
      throw new Error("Erro ao salvar alterações. Verifique permissões e dados.");
    } finally {
      setSavingByRegId((p) => ({ ...p, [regId]: false }));
    }
  };

  const renderRegistrations = useCallback(
    (row: FreelancerControlRow, regs: StoreRegistrationItem[]) => {
      const primaryRegId = String(regByFreelancerId[row.freelancerId]?.id ?? "");

      if (regs.length === 0) {
        return <div className="BoxEmpty">Nenhuma inscrição encontrada no período.</div>;
      }

      return (
        <div className="DraftList">
          {regs.map((reg) => {
            const regId = String(reg?.id ?? "");
            const current = normalizeStatus((reg as any)?.status);
            const target = regId ? (editedStatusByRegId[regId] ?? current) : current;
            const dirty = regId ? safeStatusLabel(current) !== safeStatusLabel(target) : false;
            const saving = regId ? !!savingByRegId[regId] : false;
            const slotCanceled = isRegistrationSlotCanceled(reg, row);
            const storeName = getRegistrationStoreName(reg, row);
            const finalValue = getRegistrationFinalValue(reg, row);

            return (
              <div key={regId || `${row.freelancerId}-${formatRegistrationDay(reg)}-${formatSlotRange(reg)}`} className="DraftItem">
                <div className="DraftInfo">
                  <div className="DraftTitle">
                    {formatRegistrationDay(reg)} • {formatSlotRange(reg)}
                  </div>
                  <div className="DraftSub">
                    {storeName} • {finalValue}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <StatusBadge
                    value={getStatusBadgeColor(target)}
                    title={target}
                  >
                    {target}
                  </StatusBadge>

                  <div className="ActionsBtns">
                    <button
                      type="button"
                      className="PagerButton PagerButtonLight ActionsBtn"
                      onClick={() => openStatus(row, reg)}
                      disabled={loading || saving || !regId || slotCanceled}
                      title={
                        !regId
                          ? "Sem inscrição no período"
                          : slotCanceled
                          ? "Slot cancelado"
                          : dirty
                          ? `Status selecionado: ${target}`
                          : "Selecionar status"
                      }
                      style={{ borderColor: dirty ? "#f39c12" : undefined }}
                    >
                      <img className="FreelancerSearchIcon" src={statusIcon} alt="Status" />
                    </button>

                    <button
                      type="button"
                      className="PagerButton ActionsBtn ActionsBtnConfirm"
                      onClick={() => handleConfirmRegistration(row, reg)}
                      disabled={loading || saving || !regId || slotCanceled}
                      title={
                        saving
                          ? "Salvando..."
                          : !regId
                          ? "Sem inscrição no período"
                          : slotCanceled
                          ? "Slot cancelado"
                          : "Confirmar alteração"
                      }
                    >
                      <img className="FreelancerSearchIcon" src={confirmIcon} alt="Confirmar" />
                    </button>

                    <button
                      type="button"
                      className="PagerButton PagerButtonLight ActionsBtn ActionsBtnSub"
                      onClick={() => openSub(row, reg)}
                      disabled={loading || saving || !regId || slotCanceled}
                      title={
                        !regId
                          ? "Sem inscrição no período"
                          : slotCanceled
                          ? "Slot cancelado"
                          : "Substituir freelancer"
                      }
                    >
                      <img className="FreelancerSearchIcon" src={replaceIcon} alt="Substituir" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    },
    [editedStatusByRegId, loading, openStatus, openSub, regByFreelancerId, savingByRegId],
  );

  const filtered = rows;

  if (!token) return null;

  return (
    <div className="homeLayout">
      <main className="homeMain">
        <div className="StoreMenuPage ecosystem">
          <img
            className="ecosystem__bg-img"
            src={DenseForest}
            alt=""
            aria-hidden="true"
          />
          <div className="ecosystem__content-bg" aria-hidden="true" />

          <div className="freelancer-container ecosystem__content">
            <header className="ecosystem__header">
              <h1 className="ecosystem__title">
                <span className="ecosystem__title-strong">Controle de </span>
                <span className="ecosystem__title-light">Freelancers</span>
              </h1>

              <p className="ecosystem__subtitle">Acompanhamento por período</p>

              <div className="ecosystem__actions">
                <button
                  className="ecosystem__actionBtn"
                  onClick={() => navigate(OPERACOES_PAGE())}
                  disabled={loading}
                >
                  Operações
                </button>
                <button
                  className="ecosystem__actionBtn"
                  onClick={() => navigate(SLOTS_MANAGER_PAGE())}
                  disabled={loading}
                >
                  Gestão de Horários
                </button>
              </div>
            </header>

            <div className="freelancer-card-table">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <label className="freelancer-subtitle" style={{ margin: 0 }}>
                    Período:
                  </label>
                  <input
                    className="freelancer-input"
                    type="date"
                    value={startDay}
                    onChange={(e) => {
                      setStartDay(e.target.value);
                      setPage(1);
                    }}
                    disabled={loading}
                  />
                  <span className="freelancer-subtitle" style={{ margin: 0 }}>
                    até
                  </span>
                  <input
                    className="freelancer-input"
                    type="date"
                    value={endDay}
                    onChange={(e) => {
                      setEndDay(e.target.value);
                      setPage(1);
                    }}
                    disabled={loading}
                  />

                  <button
                    type="button"
                    className="PagerButton PagerButtonLight"
                    onClick={() => setStoreModalOpen(true)}
                    disabled={loading}
                    title="Selecionar loja prioridade"
                  >
                    {priorityStore ? `Loja: ${priorityStore.nomeFantasia}` : "Filtrar por loja"}
                  </button>
                </div>

                <div className="FreelancerSearch FreelancerSearch--mobileWrap">
                  <input
                    className="freelancer-input FreelancerSearchInput"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                    placeholder="Buscar por nome"
                    disabled={loading}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        applySearch();
                      }
                    }}
                  />

                  <button
                    type="button"
                    className="PagerButton FreelancerSearchBtn"
                    onClick={applySearch}
                    disabled={loading}
                    title="Buscar"
                  >
                    <img className="FreelancerSearchIcon" src={searchIcon} alt="Buscar" />
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 16 }} />

              {loading ? (
                <div className="BoxEmpty">Carregando...</div>
              ) : filtered.length === 0 ? (
                <div className="BoxEmpty">Nenhum freelancer encontrado.</div>
              ) : (
                <>
                  <div className="ControlLegend" role="note" aria-label="Legenda de cores do acompanhamento">
                    <div className="ControlLegendItem">
                      <span className="ControlLegendBadge ControlLegendBadge--orange">LARANJA</span>
                      <span className="ControlLegendText amplesoft-regular">
                        PENDENTE
                      </span>
                    </div>

                    <div className="ControlLegendItem">
                      <span className="ControlLegendBadge ControlLegendBadge--red">VERMELHO</span>
                      <span className="ControlLegendText amplesoft-regular">
                        CANCELADO
                      </span>
                    </div>

                    <div className="ControlLegendItem">
                      <span className="ControlLegendBadge ControlLegendBadge--green">VERDE</span>
                      <span className="ControlLegendText amplesoft-regular">
                        APROVADO
                      </span>
                    </div>

                    <div className="ControlLegendItem">
                      <span className="ControlLegendBadge ControlLegendBadge--gray">CINZA</span>
                      <span className="ControlLegendText amplesoft-regular">
                        SEM AGENDA
                      </span>
                    </div>

                    <div className="ControlLegendItem">
                      <span className="ControlLegendBadge ControlLegendBadge--blue">AZUL</span>
                      <span className="ControlLegendText amplesoft-regular">
                        AUSENTE
                      </span>
                    </div>
                  </div>

                  <div className="RatingsTableWrap">
                    <table className="RatingsTable">
                      <thead>
                        <tr>
                          <th className="amplesoft">Nome completo</th>
                          <th className="amplesoft">Contato</th>
                          <th className="amplesoft">CPF</th>
                          <th className="amplesoft">Inscrições</th>
                          <th className="amplesoft RatingsStarsCol">Avaliações</th>
                        </tr>
                      </thead>

                      <tbody>
                        {filtered.map((r) => {
                          const regs = regListByFreelancerId[r.freelancerId] ?? [];
                          const expanded = expandedFreelancerId === r.freelancerId;

                          return (
                            <React.Fragment key={r.freelancerId}>
                              <tr>
                                <td className="RatingsCell RatingsNameCell">
                                  <StatusBadge
                                    value={getStatusBadgeColor(r.registrationStatus)}
                                    title={r.fullName ?? "-"}
                                    className="RatingsNameBadge"
                                  >
                                    <span className="RatingsNameText">
                                      {r.fullName ?? "-"}
                                    </span>
                                  </StatusBadge>
                                </td>

                                <td className="RatingsCell">{r.contato ?? "-"}</td>
                                <td className="RatingsCell">{formatCPF(r.cpf)}</td>
                                <td className="RatingsCell">
                                  {regs.length === 0 ? (
                                    "-"
                                  ) : (
                                    <button
                                      type="button"
                                      className="PagerButton PagerButtonLight"
                                      onClick={() =>
                                        setExpandedFreelancerId((prev) =>
                                          prev === r.freelancerId ? null : r.freelancerId
                                        )
                                      }
                                      disabled={loading}
                                    >
                                      {expanded ? "Ocultar" : regs.length > 1 ? `Ver (${regs.length})` : "Ver"}
                                    </button>
                                  )}
                                </td>

                                <td className="RatingsCell RatingsStarsCol">
                                  <Stars value={r.stars} onClick={() => openEval(r)} disabled={loading || !r.storeToWorkId} />
                                </td>
                              </tr>

                              {expanded ? (
                                <tr>
                                  <td className="RatingsCell" colSpan={5}>
                                    {renderRegistrations(r, regs)}
                                  </td>
                                </tr>
                              ) : null}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="RatingsCards">
                    <div className="DraftList">
                      {filtered.map((r) => {
                        const regs = regListByFreelancerId[r.freelancerId] ?? [];
                        const expanded = expandedFreelancerId === r.freelancerId;

                        return (
                          <div key={r.freelancerId} className="DraftItem RatingsCard">
                            <div className="DraftInfo">
                              <div className="DraftTitle">
                                <StatusBadge
                                  value={getStatusBadgeColor(r.registrationStatus)}
                                  title={
                                    r.registrationStatus
                                      ? `${r.registrationStatus}${r.slotIsCanceled ? " (SLOT CANCELADO)" : ""}`
                                      : "Sem inscrição no período"
                                  }
                                >
                                  {r.fullName}
                                </StatusBadge>
                              </div>
                              <div className="DraftSub">
                                {regs.length} inscrição{regs.length === 1 ? "" : "ões"} no período
                              </div>
                            </div>

                            <div style={{ marginTop: 10 }} />

                            <div className="SelectedInfoBox">
                              <div className="SelectedInfoRow">
                                <span className="SelectedInfoLabel">Contato</span>
                                <span className="SelectedInfoValue">{r.contato ?? "-"}</span>
                              </div>
                            </div>

                            <div className="SelectedInfoBox">
                              <div className="SelectedInfoRow">
                                <span className="SelectedInfoLabel">CPF</span>
                                <span className="SelectedInfoValue">{formatCPF(r.cpf)}</span>
                              </div>
                            </div>

                            <div className="SelectedInfoBox">
                              <div className="SelectedInfoRow">
                                <span className="SelectedInfoLabel">Avaliações</span>
                                <span className="SelectedInfoValue">
                                  <Stars value={r.stars} onClick={() => openEval(r)} disabled={loading || !r.storeToWorkId} />
                                </span>
                              </div>
                            </div>

                            <button
                              type="button"
                              className="PagerButton PagerButtonLight PresenceConfirmBtn PresenceConfirmBtnFull"
                              onClick={() =>
                                setExpandedFreelancerId((prev) =>
                                  prev === r.freelancerId ? null : r.freelancerId
                                )
                              }
                              disabled={loading || regs.length === 0}
                              style={{ marginTop: 10 }}
                            >
                              {expanded ? "Ocultar inscrições" : regs.length > 1 ? `Ver inscrições (${regs.length})` : "Ver inscrição"}
                            </button>

                            {expanded ? (
                              <div style={{ marginTop: 10 }}>
                                {renderRegistrations(r, regs)}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="Pager RatingsPager">
                    <button
                      type="button"
                      className="PagerButton PagerButtonLight"
                      disabled={loading || page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Ant.
                    </button>

                    <div className="PagerInfo">
                      {page} de {totalPages}
                    </div>

                    <button
                      type="button"
                      className="PagerButton"
                      disabled={loading || page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Próx.
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <PickStoreModal
            isOpen={storeModalOpen}
            token={token}
            onClose={() => setStoreModalOpen(false)}
            initialStoreId={priorityStore?.id ?? null}
            onPicked={(store) => {
              setPriorityStore(store);
              setPage(1);
            }}
          />

          {isEvalOpen && evalTarget ? (
            <ViewFreelancerSubmittedEvaluationsModal
              isOpen={isEvalOpen}
              onClose={closeEval}
              token={token}
              storeId={evalTarget.storeId}
              freelancerId={evalTarget.freelancerId}
              storeName={evalTarget.storeName}
              freelancerName={evalTarget.freelancerName}
              freelancerCpf={evalTarget.freelancerCpf}
              fetchSubmittedEvaluationsByRegistration={({ token, registrationId }) =>
                listSubmittedEvaluationsByRegistrationApi({ token, registrationId })
              }
            />
          ) : null}

          {isStatusOpen && statusTarget ? (
            <RegistrationStatusModal
              isOpen={isStatusOpen}
              onClose={closeStatus}
              currentStatus={
                editedStatusByRegId[String(statusTarget.reg?.id ?? "")] ??
                normalizeStatus((statusTarget.reg as any)?.status)
              }
              freelancerName={statusTarget.row.fullName ?? null}
              storeName={getRegistrationStoreName(statusTarget.reg, statusTarget.row)}
              day={(statusTarget.reg as any)?.day ?? statusTarget.reg.slot?.startAt ?? statusTarget.row.day ?? null}
              onConfirm={(nextStatus: SlotRegistrationStatus) =>
                handleConfirmRegistration(statusTarget.row, statusTarget.reg, nextStatus)
              }
            />
          ) : null}

          {isSubOpen && selectedSubReg && subStoreId && (
            <SubstituteFreelancerModal
              isOpen={isSubOpen}
              onClose={closeSub}
              storeId={subStoreId}
              registration={selectedSubReg}
              onSuccess={load}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default FreelancerControlPage;
